import { useState } from "react";
import { Link } from "react-router-dom";
import { BASE_URL } from '../apiConfig';
import SallerBank from "../cors/SallerBank";
import { useAuth } from "../context/AuthProvider";
import { toast } from 'sonner';
import DeleteModal from '../cors/DeleteModal'; 

export default function SellerList({ seller, fetchSellers, Page, searchTerm }) {
  
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const { token, role, roles } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleStatusToggle = async () => {
    setIsUpdatingStatus(true);
    const obj = {
      "user_id": seller.id,
      "status": !seller.status
    };
    
    try {
      const response = await fetch(`${BASE_URL}/api/updatesellerstatus`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(obj)
      });

      const data = await response.json();

      if (!response.ok || data.code !== 200) {
        throw new Error(data.message || 'Failed to update status');
      }

      fetchSellers(Page, searchTerm);
      toast.success(data.message || `Status updated to ${!seller.status ? "Active" : "Inactive"}`);
      
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.message || "Error updating status");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const deleteSaller = async () => {
    setIsModalOpen(false); 

    try {
      const res = await fetch(BASE_URL+`/api/users/${seller.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })

      if (res.ok) {
        toast.success("Suppression réussie");
        fetchSellers(Page, searchTerm);
      } else {
        const err = await res.json()
        toast.error("Echec de suppression");
      }
    } catch (error) {
      toast.error("Echec de suppression");
    }
  }

  const formatDate = (dateString) => {
    if (!dateString || dateString === "") return "Jamais";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatPhone = (phone) => {
    if (!phone) return "Non fourni";
    const phoneStr = phone.toString();
    return phoneStr.replace(/(\d{2})(\d{3})(\d{4})/, "($1) $2-$3");
  };

  const getActivityStatus = () => {
    if (!seller.info.last_login_at || seller.info.last_login_at === "") {
      return { 
        status: "Jamais connecté", 
        color: "text-red-600 bg-red-100",
        bg: "bg-red-600",
        tooltip: "Ce vendeur ne s'est jamais connecté"
      };
    }
    
    const lastLogin = new Date(seller.info.last_login_at);
    const now = new Date();
    const diffInSeconds = Math.floor((now - lastLogin) / 1000);
    const minutesPassed = Math.floor(diffInSeconds / 60);
    const hoursPassed = Math.floor(minutesPassed / 60);
    const daysPassed = Math.floor(hoursPassed / 24);

    if (diffInSeconds < 60) {
      return {
        status: "En ligne",
        color: "text-green-600 bg-green-100",
        bg: "bg-green-600",
        tooltip: `En ligne maintenant (${lastLogin.toLocaleString()})`
      };
    }
    if (minutesPassed < 60) {
      return {
        status: `En ligne (${minutesPassed} min)`,
        color: "text-green-600 bg-green-100",
        bg: "bg-green-600",
        tooltip: `Actif il y a ${minutesPassed} minutes`
      };
    }
    if (hoursPassed < 24) {
      return {
        status: `En ligne (${hoursPassed}h)`,
        color: "text-blue-600 bg-blue-100",
        bg: "bg-blue-600",
        tooltip: `Actif il y a ${hoursPassed} heures`
      };
    }
    if (daysPassed === 1) {
      return {
        status: "En ligne (1j)",
        color: "text-yellow-600 bg-yellow-100",
        bg: "bg-yellow-600",
        tooltip: `Actif hier (${lastLogin.toLocaleString()})`
      };
    }
    if (daysPassed < 7) {
      return {
        status: `En ligne (${daysPassed}j)`,
        color: "text-amber-600 bg-amber-100",
        bg: "bg-amber-600",
        tooltip: `Actif il y a ${daysPassed} jours`
      };
    }
    if (daysPassed < 30) {
      const weeksPassed = Math.floor(daysPassed / 7);
      return {
        status: weeksPassed === 1 ? "En ligne (1 sem.)" : `En ligne (${weeksPassed} sem.)`,
        color: "text-orange-600 bg-orange-100",
        bg: "bg-orange-600",
        tooltip: `Actif il y a ${weeksPassed} semaine${weeksPassed > 1 ? 's' : ''}`
      };
    }
    return {
      status: "Inactif",
      color: "text-red-600 bg-red-100",
      bg: "bg-red-600",
      tooltip: `Dernière activité il y a plus d'un mois (${lastLogin.toLocaleDateString()})`
    };
  };

  const activity = getActivityStatus();
  const sellerRating = seller.info.level === "Beginner" ? 1 : seller.info.level === "Intermediate" ? 2 : 3;

  return (
    <div className="bg-white rounded-3xl shadow-lg border border-gray-100 px-6 py-4 hover:shadow-xl transition-all duration-300">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        
        {/* Seller Info */}
        <div className="flex items-center  gap-4">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0">
            {seller.info.logo ? (
              <i 
                src={`${BASE_URL}/storage/${seller.info.logo}`}
                width={48}
                height={48}
                className="h-12 object-cover rounded-2xl"
                alt="logo"
              />
            ) : (
              <div className="w-12 h-12 bg-[var(--color-primary-50)] rounded-2xl flex items-center justify-center">
                <svg className="h-6 w-6 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
            )}
          </div>
          <div>
            <span className={`flex max-w-max items-center gap-1 px-1.5 py-0.5 rounded-full font-semibold text-[10px] ${activity.color}`}>
              <span className={`${activity.bg} h-1.5 w-1.5 rounded-full`}></span>
              {activity.status}
            </span>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-bold text-gray-900">{seller.info.name || seller.email}</h3>
              <p className="text-gray-600 text-sm">{seller.info.name_store || seller.email}</p>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500 flex">
                {[1, 2, 3].map((star) => (
                  <svg
                    key={star}
                    className={`h-4 w-4 ${star <= sellerRating ? "text-yellow-400" : "text-gray-300"}`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.683-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.565-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </span>
              {seller.total_sales && (
                <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full">
                  {seller.total_sales} ventes
                </span>
              )}
              {
                (role === 1 || (role === 2 && roles.update_saller_status === 1)) &&
                  <div className="flex items-end justify-end gap-1">
                    <button
                          onClick={handleStatusToggle}
                          disabled={isUpdatingStatus}
                          className={`rounded-full text-[10px] pr-0.3 pl-[4px] ml-3 font-medium transition-all duration-200 flex items-center space-x-1 ${
                            seller.status
                              ? "bg-green-500 hover:bg-green-600 text-white"
                              : "bg-red-500 hover:bg-red-600 text-white"
                          } ${isUpdatingStatus ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                        >
                          {isUpdatingStatus ? (
                            <>
                              <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              <span>Mise à jour...</span>
                            </>
                          ) : (
                            <>
                              <div className={`w-1.5 h-1.5 rounded-full ${seller.status ? "bg-white" : "bg-white"}`}></div>
                              <span>{seller.status ? "Active" : "Désactive"}</span>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M8 9l4-4 4 4m0 6l-4 4-4-4"
                                />
                              </svg>
                            </>
                          )}
                        </button>
                  </div>
              }
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Téléphone</p>
            <p className="font-medium text-xs">{formatPhone(seller.info.phone)}</p>
          </div>
          <div>
            <p className="text-gray-500">Banque</p>
            <div className="font-medium text-xs flex items-center gap-1">
              <SallerBank bank={seller.info.name_bank} />
            </div>
          </div>
          <div>
            <p className="text-gray-500">Inscrit le</p>
            <p className="font-medium text-xs">{formatDate(seller.info.created_at)}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          {
            (role === 1 || (role === 2 && roles.edit_saller === 1)) &&
              <Link href={`/sallers/${seller.id}`}>
                <button className="p-2 cursor-pointer border-2 border-gray-200 hover:border-[var(--color-primary-500)] text-gray-600 hover:text-[var(--color-primary-500)] rounded-xl transition-all duration-300" title="Modifier le vendeur">
                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              </Link>
          }
          {
            (role === 1 || (role === 2 && roles.delete_saller === 1)) &&
              <button 
                onClick={() => setIsModalOpen(true)} 
                className="p-1.5 border-2 border-gray-200 cursor-pointer hover:border-red-400 text-gray-600 hover:text-red-500 rounded-xl transition-all duration-300"
                title="Voir les détails"
              >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2
                    2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1
                    1 0 00-1-1h-4a1 1 0 00-1 1v3m5 0H6"
                  />
                </svg>
              </button>
          }
        </div>
      </div>
      {
        isModalOpen && 
          <DeleteModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onConfirm={deleteSaller}
            title="Supprimer le vendeur"
            message="Êtes-vous sûr de vouloir supprimer le Vendeur"
            itemName={seller.info.name}
          />
      }
    </div>
  );
}
